import miAnalysisSkillContract from './contracts/miAnalysis.contract';
import sensiIaSkillContract from './contracts/sensiIa.contract';
import pubgMobileCalculatorSkillContract from './contracts/pubgMobileCalculator.contract';
import freefireCalculatorSkillContract from './contracts/freefireCalculator.contract';
import coachTrainingSkillContract from './contracts/coachTraining.contract';

export const GPH_SKILL_CONTRACTS = [
  miAnalysisSkillContract,
  sensiIaSkillContract,
  pubgMobileCalculatorSkillContract,
  freefireCalculatorSkillContract,
  coachTrainingSkillContract
];

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const cloneArray = (value) => (Array.isArray(value) ? [...value] : []);

const hasCapabilityId = (skill, capabilityId) => {
  const mapping = skill?.capabilityMapping || {};
  return mapping.primaryCapabilityId === capabilityId
    || cloneArray(mapping.relatedCapabilityIds).includes(capabilityId);
};

export const listSkills = () => [...GPH_SKILL_CONTRACTS];

export const getSkillById = (skillId) => (
  GPH_SKILL_CONTRACTS.find((skill) => skill.id === skillId) || null
);

export const getSkillByCapabilityId = (capabilityId) => (
  GPH_SKILL_CONTRACTS.find((skill) => hasCapabilityId(skill, capabilityId)) || null
);

export const getSkillsByGame = (game) => {
  const normalizedGame = normalizeText(game);
  return GPH_SKILL_CONTRACTS.filter((skill) => (
    cloneArray(skill.supportedGames).includes(normalizedGame)
  ));
};

export const getSkillsByIntentExample = (text) => {
  const normalizedText = normalizeText(text);
  if (!normalizedText) return [];

  return GPH_SKILL_CONTRACTS.filter((skill) => (
    cloneArray(skill.intentExamples).some((example) => {
      const normalizedExample = normalizeText(example);
      return normalizedExample.includes(normalizedText)
        || normalizedText.includes(normalizedExample);
    })
  ));
};

export const getRequiredSlots = (skillId, modeOrIntent = null) => {
  const skill = getSkillById(skillId);
  if (!skill) return [];

  if (modeOrIntent && skill.requiredSlotsByMode?.[modeOrIntent]) {
    return [...skill.requiredSlotsByMode[modeOrIntent]];
  }

  if (modeOrIntent && skill.requiredSlotsByIntent?.[modeOrIntent]) {
    return [...skill.requiredSlotsByIntent[modeOrIntent]];
  }

  if (Array.isArray(skill.requiredSlots)) {
    return [...skill.requiredSlots];
  }

  if (skill.requiredSlotsByMode) {
    return { ...skill.requiredSlotsByMode };
  }

  if (skill.requiredSlotsByIntent) {
    return { ...skill.requiredSlotsByIntent };
  }

  return [];
};

export const getOptionalSlots = (skillId) => {
  const skill = getSkillById(skillId);
  return skill ? cloneArray(skill.optionalSlots) : [];
};

export const getSlotSchema = (skillId, slotName) => {
  const skill = getSkillById(skillId);
  return skill?.slotSchemas?.[slotName] || null;
};

export const getCapabilityMapping = (skillId) => {
  const skill = getSkillById(skillId);
  if (!skill?.capabilityMapping) return null;

  return {
    primaryCapabilityId: skill.capabilityMapping.primaryCapabilityId,
    relatedCapabilityIds: cloneArray(skill.capabilityMapping.relatedCapabilityIds)
  };
};

export const validateSkillContract = (skill) => {
  const errors = [];

  if (!skill || typeof skill !== 'object') {
    return { valid: false, errors: ['skill must be an object'] };
  }

  const requiredStringFields = ['id', 'version', 'name', 'domain', 'description'];
  requiredStringFields.forEach((field) => {
    if (!skill[field] || typeof skill[field] !== 'string') {
      errors.push(`${field} must be a non-empty string`);
    }
  });

  const requiredArrayFields = ['supportedGames', 'intentExamples', 'optionalSlots'];
  requiredArrayFields.forEach((field) => {
    if (!Array.isArray(skill[field])) {
      errors.push(`${field} must be an array`);
    }
  });

  if (!Array.isArray(skill.requiredSlots) && !skill.requiredSlotsByMode && !skill.requiredSlotsByIntent) {
    errors.push('skill must define requiredSlots, requiredSlotsByMode, or requiredSlotsByIntent');
  }

  ['slotSchemas', 'askForMissingSlots', 'execution', 'output', 'guardrails', 'capabilityMapping'].forEach((field) => {
    if (!skill[field] || typeof skill[field] !== 'object' || Array.isArray(skill[field])) {
      errors.push(`${field} must be an object`);
    }
  });

  if (!skill.capabilityMapping?.primaryCapabilityId) {
    errors.push('capabilityMapping.primaryCapabilityId is required');
  }

  if (
    skill.capabilityMapping
    && skill.capabilityMapping.relatedCapabilityIds
    && !Array.isArray(skill.capabilityMapping.relatedCapabilityIds)
  ) {
    errors.push('capabilityMapping.relatedCapabilityIds must be an array when present');
  }

  if (!skill.execution?.handler || typeof skill.execution.handler !== 'string') {
    errors.push('execution.handler must be a non-empty string');
  }

  if (typeof skill.execution?.requiresBackend !== 'boolean') {
    errors.push('execution.requiresBackend must be boolean');
  }

  if (!skill.execution?.costClass || !['low', 'medium', 'high'].includes(skill.execution.costClass)) {
    errors.push('execution.costClass must be low, medium, or high');
  }

  if (!skill.output?.mode || typeof skill.output.mode !== 'string') {
    errors.push('output.mode must be a non-empty string');
  }

  if (!skill.output?.schema || typeof skill.output.schema !== 'string') {
    errors.push('output.schema must be a non-empty string');
  }

  if (JSON.stringify(skill).includes('cod_mobile')) {
    errors.push('contract must not use cod_mobile');
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

export default {
  listSkills,
  getSkillById,
  getSkillByCapabilityId,
  getSkillsByGame,
  getSkillsByIntentExample,
  getRequiredSlots,
  getOptionalSlots,
  getSlotSchema,
  getCapabilityMapping,
  validateSkillContract
};
